import { CircularProgress, Grid, Paper } from '@mui/material';
import { ColumnSizingState, Row, RowModel, RowSelectionState } from '@tanstack/react-table';
import { Formik, FormikProps } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from 'store/actions/messageActions';
import store from 'store/store';
import { Container, GetContainersByVoyageParams } from '../../global/adapters/crpAPI';
import { DataGrid } from '../../global/components';
import { Drawer } from '../../global/components/Drawer';
import EmptyState from '../../global/components/EmptyState/EmptyState';
import { useLocalTableValues } from '../../global/hooks';
import { useGetContainersAssignReleaseParty } from '../adapters/assign-release-party/queries';
import { StepperAssignReleaseParty } from '../components/assign-release-party';
import ContainerDetailDrawer from '../components/shared/ContainerDetailDrawer';
import { baseShippingLineColumnsWithCheckbox } from '../constants/shared/shippingLineColumns';
import { getSelectedUserType } from 'store/selectors/selectors';
import { ConnectedProps, connect } from 'react-redux';
import { useGetContainersByDashboardMetric } from '../adapters/dashboard/queries';
import { DashboardMetricType } from 'modules/transport-operator/adapters/dashboard/queries';
import { isFromDashboard } from 'modules/global/helpers/searchParamsHelper';

type Props = React.ComponentPropsWithoutRef<'div'> & {};
interface IPropFromRedux extends PropsFromRedux {
  selectedUserType: any;
}

interface IProps extends IPropFromRedux, Props {}

const initialFields: GetContainersByVoyageParams = {
  vesselName: '',
  voyageNumber: '',
  imoNumber: '',
  billOfLadingNumber: '',
  equipmentNumber: '',
  stayNumber: '',
  isArchived: false,
};

const AssignReleaseParty: React.FunctionComponent<IProps> = (props) => {
  const { t } = useTranslation(['assignReleaseParty', 'common', 'validation']);
  const formRef = useRef<FormikProps<GetContainersByVoyageParams>>();

  const [userType] = React.useState(props.selectedUserType);
  const [displayGrid, setDisplayGrid] = React.useState(false);
  const [searchPerformed, setSearchPerformed] = React.useState(false);
  const [showDetail, setShowDetail] = React.useState<{
    open: boolean;
    data?: Container;
  }>({
    open: false,
    data: undefined,
  });

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const getRowSelection = React.useRef<(() => RowModel<Container>) | null>(null);
  const [selectedRows, setSelectedRows] = React.useState<Row<Container>[] | null>(null);

  const [columnSizing, setColumnSizing] = useLocalTableValues<ColumnSizingState>(
    'column-sizing-assign-release-party',
    {}
  );

  const containersByDashboardMetric = useGetContainersByDashboardMetric(
    DashboardMetricType.DeclinedReleaseRight,
    isFromDashboard()
  );

  const containersForAssignReleaseParty = useGetContainersAssignReleaseParty({
    vesselName: formRef?.current?.values.vesselName,
    voyageNumber: formRef?.current?.values.voyageNumber,
    imoNumber: formRef?.current?.values.imoNumber,
    stayNumber: formRef?.current?.values.stayNumber,
    isArchived: formRef?.current?.values.isArchived,
    billOfLadingNumber: formRef?.current?.values.billOfLadingNumber,
    equipmentNumber: formRef?.current?.values.equipmentNumber,
  });

  const { data, isLoading, isFetching, isError } =
    isFromDashboard() && !searchPerformed
      ? containersByDashboardMetric
      : containersForAssignReleaseParty;

  const handleOnSubmit = () => {
    setDisplayGrid(true);
    setRowSelection({});
    setSearchPerformed(true);
  };

  React.useEffect(() => {
    isError && store.dispatch(errorMessage(t('validation:errorGetContainers')));
  }, [isError, t]);

  React.useEffect(() => {
    if (!isFetching && data) {
      setDisplayGrid(true);
    }
  }, [isFetching, data]);

  React.useEffect(() => {
    if (getRowSelection.current !== null) {
      setSelectedRows(getRowSelection.current().rows);
    }
  }, [rowSelection]);

  const handleCellClick = (myRowData: Container) => {
    setShowDetail({ open: true, data: myRowData });
  };

  return (
    <Grid container spacing={2} style={{ margin: '20px 0 0 5px' }}>
      <Grid size={{ xs: 4, sm: 3 }}>
        <Formik
          initialValues={initialFields}
          innerRef={formRef as React.Ref<FormikProps<GetContainersByVoyageParams>>}
          onSubmit={handleOnSubmit}
        >
          {({
            errors,
            submitForm,
            resetForm,
            values,
          }: FormikProps<GetContainersByVoyageParams>) => {
            return (
              <StepperAssignReleaseParty
                errors={errors}
                selectedRows={selectedRows}
                setDisplayGrid={setDisplayGrid}
                data={data}
                values={values}
                isFetching={isFetching}
                resetForm={resetForm}
                submitForm={submitForm}
                selectedUserType={userType}
                skipSearchStep={isFromDashboard()}
              />
            );
          }}
        </Formik>
      </Grid>
      <Grid size={{ xs: 8, sm: 9 }}>
        {isFetching ? (
          <CircularProgress />
        ) : isError ? (
          <EmptyState title={t('validation:errorGetContainers')} />
        ) : displayGrid && data ? (
          <React.Fragment>
            <Paper>
              <DataGrid.Client
                columns={baseShippingLineColumnsWithCheckbox}
                columnSizing={columnSizing}
                data={data}
                getRowSelection={getRowSelection}
                isLoading={isLoading}
                onCellClick={handleCellClick}
                onColumnSizingChange={setColumnSizing}
                onRowSelectionChange={setRowSelection}
                rowSelection={rowSelection}
              />
            </Paper>
            <Drawer setShowDrawer={setShowDetail} showDrawer={showDetail.open}>
              {showDetail.data !== undefined && (
                <ContainerDetailDrawer containerId={showDetail.data.releaseId} />
              )}
            </Drawer>
          </React.Fragment>
        ) : (
          <EmptyState title={t('emptyState.title')} subTitle={t('emptyState.subTitle')} />
        )}
      </Grid>
    </Grid>
  );
};

const mapState = (state: any) => {
  return {
    selectedUserType: getSelectedUserType(state),
  };
};

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AssignReleaseParty);
